// NATIVE_TRACKING_V10
import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapPin } from "lucide-react";
import { dms } from "./api";
import { useAuth } from "@/context/AuthContext";

const FIELD_ROLES = ["salesperson", "team_leader", "regional_manager"];
const CHECK_MS = 30000;       // GPS check; high-accuracy fixes are the main battery cost
const MOVING_PING_MS = 30000; // moving: a ping this often
const STILL_PING_MS = 120000; // standing still: a heartbeat this often
const FAR_MOVE_M = 50;        // moved this far: ping right away...
const FAR_MOVE_GAP_MS = 20000; // ...but not more often than this
const MOVING_M = 15;          // less than this since the last ping is GPS jitter, not movement
const PLUGIN_DISTANCE_M = 20; // plugin callback fires after this much movement
const RETRY_MS = 30000;       // a failed ping is retried after this long
const GOOD_FIX_M = 100;     // a worse fix is never sent (same limit as the server)
const OFF_FAILS = 3;        // this many "unavailable" errors in a row means location is off
const RECENT_FIX_MS = 45000; // a fix this recent proves location is on, whatever the error says

const isNative = () => {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch (e) { return false; }
};
const bgPlugin = () => {
  try {
    return isNative() && window.Capacitor.Plugins && window.Capacitor.Plugins.BackgroundGeolocation
      ? window.Capacitor.Plugins.BackgroundGeolocation : null;
  } catch (e) { return null; }
};
const metres = (lat1, lng1, lat2, lng2) => {
  const R = 6371000, rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
  const x = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(x));
};

// Runs on every DMS page for field staff. While punched in:
//  - browser: pings from a 30s GPS check, app locked if location is off
//  - Android app: pings from the background location service AND the 30s GPS check,
//    so a silent plugin callback does not stop them; auto punch-out if location is turned off
//  A heartbeat repeats the last good point. If that point is old it is marked stale, so the
//  server does not treat it as the person's last real location.
export default function FieldLocationGuard() {
  const { user, impersonation, logout } = useAuth();
  // Owner viewing via "Login As" is just inspecting the account, not actually
  // out in the field, so never ask for or enforce location during impersonation.
  const isField = !!user && !impersonation && FIELD_ROLES.includes(user.role);
  const [punchedIn, setPunchedIn] = useState(false);
  const [autoOut, setAutoOut] = useState(null); // reason text when the last punch was closed automatically
  const [problem, setProblem] = useState(null); // null | "blocked" | "off"
  const [reBusy, setReBusy] = useState(false);
  const [reMsg, setReMsg] = useState("");
  const failRef = useRef(0);
  const busyRef = useRef(false);
  const autoSentRef = useRef(false);
  const lastFixRef = useRef(0);
  // True from the moment the phone is locked until a good fix arrives with the app visible.
  const lockedRef = useRef(false);
  const noteFix = useCallback((accuracy) => {
    lastFixRef.current = Date.now();
    const good = accuracy == null || accuracy <= GOOD_FIX_M;
    if (good && document.visibilityState === "visible") lockedRef.current = false;
  }, []);
  const sentRef = useRef({ lat: null, lng: null, at: 0 });
  const goodFixRef = useRef(null);
  const problemRef = useRef(null);
  problemRef.current = problem;
  const postPing = useCallback((fix, stale) => {
    const sent = sentRef.current;
    const now = Date.now();
    sent.at = now;
    sent.lat = fix.latitude;
    sent.lng = fix.longitude;
    const body = {
      lat: fix.latitude, lng: fix.longitude, accuracy: fix.accuracy, speed: fix.speed,
      fix_age_s: Math.round((now - fix.at) / 1000),
    };
    if (stale) body.stale = true;
    dms.trackingPing(body).catch(() => {
      if (sent.at === now) sent.at = now - STILL_PING_MS + RETRY_MS;
    });
  }, []);
  // Both location sources feed this one function, so they share the same timing.
  const sendFix = useCallback(({ latitude, longitude, accuracy, speed }) => {
    if (accuracy != null && accuracy > GOOD_FIX_M) return;
    const fix = { latitude, longitude, accuracy, speed, at: Date.now() };
    goodFixRef.current = fix;
    const sent = sentRef.current;
    const elapsed = fix.at - sent.at;
    const moved = sent.lat == null ? Infinity : metres(sent.lat, sent.lng, latitude, longitude);
    const due = (moved >= FAR_MOVE_M && elapsed >= FAR_MOVE_GAP_MS)
      || (moved >= MOVING_M && elapsed >= MOVING_PING_MS)
      || elapsed >= STILL_PING_MS;
    if (due) postPing(fix, false);
  }, [postPing]);
  // Runs on its own timer, so it does not need a new fix or a plugin callback.
  const sendHeartbeat = useCallback(() => {
    const fix = goodFixRef.current;
    const now = Date.now();
    if (!fix || problemRef.current || now - sentRef.current.at < STILL_PING_MS) return;
    postPing(fix, now - fix.at >= STILL_PING_MS);
  }, [postPing]);
  const [perm, setPerm] = useState(null); // "prompt" | "granted" | "denied"
  useEffect(() => {
    if (!isField || !navigator.permissions || !navigator.permissions.query) return undefined;
    let st = null; let dead = false;
    navigator.permissions.query({ name: "geolocation" }).then((r) => {
      if (dead) return;
      st = r; setPerm(r.state); r.onchange = () => setPerm(r.state);
    }).catch(() => {});
    return () => { dead = true; if (st) st.onchange = null; };
  }, [isField]);
  const askNow = () => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(() => setPerm("granted"), () => {}, { enableHighAccuracy: true, timeout: 15000 });
  };

  const refreshPunch = useCallback(() => {
    if (!isField) return;
    dms.punchToday()
      .then((d) => {
        const p = d && d.punch;
        setPunchedIn(!!(p && p.in_at && !p.out_at));
        setAutoOut(p && p.in_at && p.out_at && p.auto_out ? (p.auto_out_reason || "Punched out automatically") : null);
      })
      .catch(() => {});
  }, [isField]);

  useEffect(() => {
    if (!isField) return undefined;
    refreshPunch();
    const t = setInterval(refreshPunch, 30000);
    const onChange = () => refreshPunch();
    window.addEventListener("dms-punch-changed", onChange);
    return () => { clearInterval(t); window.removeEventListener("dms-punch-changed", onChange); };
  }, [isField, refreshPunch]);

  // ---- Android app: background location service while punched in ----
  useEffect(() => {
    if (!isField || !punchedIn) return undefined;
    const BG = bgPlugin();
    if (!BG) return undefined;
    let dead = false;
    let wid = null;
    let ret;
    try {
    ret = BG.addWatcher(
      {
        backgroundTitle: "GoOil DMS - tracking is ON",
        backgroundMessage: "Your location is shared while you are punched in. Keep location ON.",
        requestPermissions: true,
        stale: false,
        distanceFilter: PLUGIN_DISTANCE_M,
      },
      (loc, err) => {
        if (dead) return;
        if (err) { if (err.code === "NOT_AUTHORIZED") setProblem("blocked"); return; }
        if (!loc) return;
        noteFix(loc.accuracy);
        sendFix(loc);
      }
    );
    } catch (e) { return undefined; }
    // Kuch APK versions Promise dete hain, kuch seedha watcher id. Dono chalenge.
    Promise.resolve(ret).then((id) => {
      if (id && typeof id === "object" && id.id != null) id = id.id;
      if (id == null) return;
      if (dead) { try { BG.removeWatcher({ id }); } catch (e) { /* ignore */ } } else { wid = id; }
    }).catch(() => {});
    return () => {
      dead = true;
      if (wid != null) { try { BG.removeWatcher({ id: wid }); } catch (e) { /* ignore */ } }
    };
  }, [isField, punchedIn, noteFix, sendFix]);

  const check = useCallback(() => {
    if (busyRef.current) return;
    if (!("geolocation" in navigator)) return;
    busyRef.current = true;
    const release = setTimeout(() => { busyRef.current = false; }, 20000);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(release);
        busyRef.current = false;
        failRef.current = 0;
        setProblem(null);
        noteFix(pos.coords.accuracy);
        sendFix(pos.coords);
      },
      (err) => {
        clearTimeout(release);
        busyRef.current = false;
        const code = err && err.code;
        if (code === 1) {
          const q = navigator.permissions && navigator.permissions.query
            ? navigator.permissions.query({ name: "geolocation" }) : null;
          if (q) q.then((st) => setProblem(st.state === "denied" ? "blocked" : "off")).catch(() => setProblem("blocked"));
          else setProblem("blocked");
        } else if (code === 2) {
          if (Date.now() - lastFixRef.current < RECENT_FIX_MS) {
            failRef.current = 0;
            return;
          }
          failRef.current += 1;
          if (failRef.current >= OFF_FAILS) setProblem("off");
        }
        // code 3 (timeout / weak signal): do not lock the app
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );
  }, [noteFix, sendFix]);

  useEffect(() => {
    if (!isField || !punchedIn) { setProblem(null); failRef.current = 0; return undefined; }
    if (document.visibilityState !== "visible") lockedRef.current = true;
    sentRef.current = { lat: null, lng: null, at: 0 };
    goodFixRef.current = null;
    check();
    const t = setInterval(() => { sendHeartbeat(); check(); }, CHECK_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") check();
      else lockedRef.current = true;
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", check);
    };
  }, [isField, punchedIn, check, sendHeartbeat]);

  // ---- Android app: location turned off / blocked -> punch out automatically ----
  useEffect(() => {
    if (!punchedIn) { autoSentRef.current = false; return; }
    if (!problem || !isNative() || autoSentRef.current) return;
    autoSentRef.current = true;
    dms.punchAutoOut({ reason: "location_off", locked: lockedRef.current })
      .catch(() => {})
      .finally(() => {
        refreshPunch();
        window.dispatchEvent(new Event("dms-punch-changed"));
      });
  }, [problem, punchedIn, refreshPunch]);

  const punchInAgain = async () => {
    setReBusy(true);
    setReMsg("");
    try {
      const pos = await new Promise((res, rej) => {
        if (!("geolocation" in navigator)) { rej({ code: 0 }); return; }
        navigator.geolocation.getCurrentPosition(res, rej, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
      });
      const body = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy, via_app: isNative() };
      await (user.role === "team_leader" ? dms.tlPunchIn(body) : dms.punchIn(body));
      window.dispatchEvent(new Event("dms-punch-changed"));
      refreshPunch();
    } catch (e) {
      const code = e && e.code;
      setReMsg(
        (e && e.response && e.response.data && e.response.data.detail)
        || (code === 1 ? "Location permission is blocked. Allow location for this app in the phone settings, then try again."
          : code === 2 ? "Phone location is OFF. Pull down the top bar, turn Location ON, then try again."
          : "Could not get your location. Go to an open place and try again.")
      );
    } finally {
      setReBusy(false);
    }
  };

  if (isField && !punchedIn && autoOut) {
    return (
      <div
        role="alertdialog" aria-modal="true" data-testid="auto-punchout-block"
        style={{ position: "fixed", inset: 0, zIndex: 2147483000, background: "rgba(2,6,23,0.98)",
                 color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                 padding: 24, textAlign: "center" }}
      >
        <div style={{ maxWidth: 420 }}>
          <MapPin size={56} color="#f87171" style={{ margin: "0 auto 16px" }} />
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>You were punched out</h2>
          <p style={{ marginBottom: 8, lineHeight: 1.5, color: "#fca5a5", fontWeight: 600 }}>Reason: {autoOut}</p>
          <p style={{ marginBottom: 20, lineHeight: 1.5, color: "#cbd5e1" }}>
            Turn Location ON, then Punch In again to use the app. The Owner and HR can see this reason.
          </p>
          {reMsg && <p style={{ marginBottom: 12, color: "#fecaca" }}>{reMsg}</p>}
          <button
            onClick={punchInAgain} disabled={reBusy} data-testid="auto-punchout-punchin"
            style={{ background: "#059669", color: "#fff", border: 0, borderRadius: 8,
                     padding: "12px 20px", fontSize: 16, fontWeight: 600, width: "100%", opacity: reBusy ? 0.6 : 1 }}
          >
            {reBusy ? "Getting your location..." : "Punch In again"}
          </button>
          {logout && (
            <button
              onClick={() => logout()}
              style={{ marginTop: 12, background: "transparent", color: "#94a3b8", border: "1px solid #475569",
                       borderRadius: 8, padding: "10px 20px", fontSize: 14, width: "100%" }}
            >
              Log out
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isField && !punchedIn && (perm === "prompt" || perm === "denied")) {
    return (
      <div data-testid="location-banner"
        style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 2147482000,
                 background: "#065f46", color: "#fff", padding: "12px 16px", fontSize: 14,
                 display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200, lineHeight: 1.4 }}>
          {perm === "denied"
            ? "Location is blocked. Chrome > 3 dots > Settings > Site settings > Location > find this site > Allow. Then open the app again."
            : "Allow location so you can Punch In and be tracked."}
        </div>
        {perm === "prompt" && (
          <button onClick={askNow}
            style={{ background: "#fff", color: "#065f46", border: 0, borderRadius: 8,
                     padding: "10px 16px", fontWeight: 700 }}>
            Allow location
          </button>
        )}
      </div>
    );
  }
  if (!isField || !punchedIn || !problem) return null;

  const blocked = problem === "blocked";
  return (
    <div
      role="alertdialog" aria-modal="true" data-testid="location-block"
      style={{ position: "fixed", inset: 0, zIndex: 2147483000, background: "rgba(2,6,23,0.98)",
               color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
               padding: 24, textAlign: "center" }}
    >
      <div style={{ maxWidth: 420 }}>
        <MapPin size={56} color="#f87171" style={{ margin: "0 auto 16px" }} />
        <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>
          {blocked ? "Location permission is blocked" : "Location is OFF"}
        </h2>
        <p style={{ marginBottom: 12, lineHeight: 1.5 }}>
          You are punched in, so your location must stay ON. The app is locked until it is back.
        </p>
        <p style={{ marginBottom: 20, lineHeight: 1.5, color: "#cbd5e1" }}>
          {blocked
            ? "Open Chrome > 3 dots > Settings > Site settings > Location, find this site, set it to Allow. Then come back here."
            : "Pull down the top bar of your phone and turn Location ON. Then come back here."}
        </p>
        <button
          onClick={() => { failRef.current = 0; check(); }}
          style={{ background: "#059669", color: "#fff", border: 0, borderRadius: 8,
                   padding: "12px 20px", fontSize: 16, fontWeight: 600, width: "100%" }}
        >
          I turned it ON - check again
        </button>
        <p style={{ marginTop: 16, fontSize: 13, color: "#94a3b8" }}>
          To stop tracking, turn Location ON and then Punch Out from the Attendance page.
        </p>
      </div>
    </div>
  );
}
